/**
 * SEO HEADING STRUCTURE - Study With Kpop (Hub Page)
 * ==================================================
 * 
 * TITLE: Study With Kpop | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Study with your favorite K-Pop groups...
 * 
 * H1 (1 tag):
 *   - "Study With K-Pop" (hero section)
 * 
 * H2 (3 tags):
 *   - "Featured Groups" (featured K-Pop groups section)
 *   - "All K-Pop Study Rooms" (full groups list section)
 *   - "Start Your K-Pop Study Session" (CTA section)
 * 
 * H3 (20+ tags):
 *   - Individual K-Pop group name cards (20 groups total)
 */

import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Music, Heart, Users, Sparkles, ArrowRight, Play, Search, Star } from "lucide-react";
import { Link } from "react-router-dom";

// Import available images
import skzImage from "@/assets/skz-study-session-1.jpg";
import itzyImage from "@/assets/itzy-group-photo-1.webp";
import blackpinkImage from "@/assets/blackpink-group-photo-1.jpg";
import aespaImage from "@/assets/aespa-group-photo-1.webp";
import twiceImage from "@/assets/kpop-study-hero.jpg";
import ateezImage from "@/assets/ateez-group-photo-3.png";

interface KpopGroup {
  name: string;
  shortName: string;
  fandom: string;
  link: string;
  gradient: string;
  borderColor: string;
  image?: string;
  featured?: boolean;
}

const kpopGroups: KpopGroup[] = [
  { name: "Stray Kids", shortName: "SKZ", fandom: "STAY", link: "/study-with-skz/", gradient: "from-red-500/20 to-orange-500/20", borderColor: "border-red-500/30 hover:border-red-500/60", image: skzImage, featured: true },
  { name: "ITZY", shortName: "ITZY", fandom: "MIDZY", link: "/study-with-itzy/", gradient: "from-purple-500/20 to-pink-500/20", borderColor: "border-purple-500/30 hover:border-purple-500/60", image: itzyImage, featured: true },
  { name: "BLACKPINK", shortName: "BP", fandom: "BLINK", link: "/study-with-blackpink/", gradient: "from-pink-500/20 to-rose-500/20", borderColor: "border-pink-500/30 hover:border-pink-500/60", image: blackpinkImage, featured: true },
  { name: "aespa", shortName: "æspa", fandom: "MY", link: "/study-with-aespa/", gradient: "from-violet-500/20 to-indigo-500/20", borderColor: "border-violet-500/30 hover:border-violet-500/60", image: aespaImage, featured: true },
  { name: "TWICE", shortName: "TWICE", fandom: "ONCE", link: "/study-with-twice/", gradient: "from-amber-500/20 to-yellow-500/20", borderColor: "border-amber-500/30 hover:border-amber-500/60", image: twiceImage, featured: true },
  { name: "ATEEZ", shortName: "ATZ", fandom: "ATINY", link: "/study-with-ateez/", gradient: "from-blue-500/20 to-cyan-500/20", borderColor: "border-blue-500/30 hover:border-blue-500/60", image: ateezImage, featured: true },
  { name: "BTS", shortName: "BTS", fandom: "ARMY", link: "/study-with-bts/", gradient: "from-purple-500/20 to-violet-500/20", borderColor: "border-purple-500/30 hover:border-purple-500/60" },
  { name: "ENHYPEN", shortName: "ENHYPEN", fandom: "ENGENE", link: "/study-with-enhypen/", gradient: "from-orange-500/20 to-red-500/20", borderColor: "border-orange-500/30 hover:border-orange-500/60" },
  { name: "NCT", shortName: "NCT", fandom: "NCTzen", link: "/study-with-nct/", gradient: "from-green-500/20 to-emerald-500/20", borderColor: "border-green-500/30 hover:border-green-500/60" },
  { name: "TXT", shortName: "TXT", fandom: "MOA", link: "/study-with-txt/", gradient: "from-sky-500/20 to-blue-500/20", borderColor: "border-sky-500/30 hover:border-sky-500/60" },
  { name: "SEVENTEEN", shortName: "SVT", fandom: "CARAT", link: "/study-with-seventeen/", gradient: "from-rose-500/20 to-pink-500/20", borderColor: "border-rose-500/30 hover:border-rose-500/60" },
  { name: "EXO", shortName: "EXO", fandom: "EXO-L", link: "/study-with-exo/", gradient: "from-slate-500/20 to-gray-500/20", borderColor: "border-slate-500/30 hover:border-slate-500/60" },
  { name: "MONSTA X", shortName: "MX", fandom: "Monbebe", link: "/study-with-monsta-x/", gradient: "from-red-500/20 to-rose-500/20", borderColor: "border-red-500/30 hover:border-red-500/60" },
  { name: "THE BOYZ", shortName: "TBZ", fandom: "THE B", link: "/study-with-the-boyz/", gradient: "from-yellow-500/20 to-orange-500/20", borderColor: "border-yellow-500/30 hover:border-yellow-500/60" },
  { name: "TREASURE", shortName: "TREASURE", fandom: "TEUME", link: "/study-with-treasure/", gradient: "from-blue-500/20 to-indigo-500/20", borderColor: "border-blue-500/30 hover:border-blue-500/60" },
  { name: "GOT7", shortName: "GOT7", fandom: "Ahgase", link: "/study-with-got7/", gradient: "from-green-500/20 to-teal-500/20", borderColor: "border-green-500/30 hover:border-green-500/60" },
  { name: "SF9", shortName: "SF9", fandom: "FANTASY", link: "/study-with-sf9/", gradient: "from-amber-500/20 to-orange-500/20", borderColor: "border-amber-500/30 hover:border-amber-500/60" },
  { name: "LOONA", shortName: "LOONA", fandom: "Orbit", link: "/study-with-loona/", gradient: "from-fuchsia-500/20 to-pink-500/20", borderColor: "border-fuchsia-500/30 hover:border-fuchsia-500/60" },
  { name: "Red Velvet", shortName: "RV", fandom: "ReVeluv", link: "/study-with-red-velvet/", gradient: "from-red-500/20 to-pink-500/20", borderColor: "border-red-500/30 hover:border-red-500/60" },
  { name: "MAMAMOO", shortName: "MMM", fandom: "MooMoo", link: "/study-with-mamamoo/", gradient: "from-emerald-500/20 to-green-500/20", borderColor: "border-emerald-500/30 hover:border-emerald-500/60" },
];

const StudyWithKpop = () => {
  const [searchQuery, setSearchQuery] = useState("");
  
  const featuredGroups = useMemo(() => kpopGroups.filter(group => group.featured), []);
  
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return kpopGroups;
    const query = searchQuery.toLowerCase();
    return kpopGroups.filter(group => 
      group.name.toLowerCase().includes(query) ||
      group.shortName.toLowerCase().includes(query) ||
      group.fandom.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study With Kpop - K-Pop Themed Study Rooms"
        description="Study with your favorite K-Pop artists. Join virtual study rooms for BTS, Stray Kids, BLACKPINK, TWICE, aespa, SEVENTEEN, NCT, and 13 more K-Pop groups worldwide."
        keywords="study with kpop, kpop study room, study with bts, study with stray kids, study with blackpink, study with twice, study with nct, kpop playlist study"
        canonicalUrl="/study-with-kpop/"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">STUDY WITH YOUR FAVORITE ARTISTS</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Study With <span className="text-gradient">K-Pop</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Join virtual study rooms inspired by your favorite K-Pop artists. Stay motivated, focused, and productive while surrounded by your fandom community.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start Studying <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary fill-primary" />
                  <span className="font-medium">Free Forever</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="font-medium">Global Fandoms</span>
                </div>
              </div>
            </div>

            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Groups Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background to-muted/30" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Star className="w-4 h-4 text-primary fill-primary" />
                <span className="text-sm font-semibold text-primary">FEATURED</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Popular <span className="text-gradient">Study Rooms</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Our most loved K-Pop themed study rooms with active communities.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredGroups.map((group, index) => (
                <Link 
                  key={group.name} 
                  to={group.link}
                  className="block group"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  <Card 
                    className="overflow-hidden border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl animate-fade-in"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <div className="relative aspect-[16/10]">
                      {group.image ? (
                        <img 
                          src={group.image} 
                          alt={`Study with ${group.name}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${group.gradient} flex items-center justify-center`}>
                          <Music className="w-16 h-16 text-primary/50" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                      <div className="absolute top-3 right-3">
                        <span className="px-3 py-1 rounded-full bg-primary/90 text-primary-foreground text-xs font-semibold">
                          {group.fandom}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <h3 className="font-display text-2xl font-bold text-white mb-1 group-hover:text-primary transition-colors">
                          {group.name}
                        </h3>
                        <div className="flex items-center gap-2 text-white/80">
                          <span className="text-sm">Enter Study Room</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Search & All Groups Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/30 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Find Your <span className="text-gradient">Artist</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Search by group name, short name, or fandom name to find your perfect study room.
              </p>
              
              {/* Search Bar */}
              <div className="max-w-md mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search K-Pop groups (e.g., BTS, ARMY, SKZ...)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 py-6 text-lg bg-card/50 border-border/50 focus:border-primary/50"
                />
              </div>
            </div>

            {filteredGroups.length === 0 ? (
              <div className="text-center py-12">
                <Music className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-xl text-muted-foreground">No groups found matching "{searchQuery}"</p>
                <p className="text-muted-foreground mt-2">Try searching by group name, short name, or fandom name</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredGroups.map((group, index) => (
                  <Link 
                    key={group.name} 
                    to={group.link}
                    className="block group"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    <Card 
                      className={`overflow-hidden ${group.borderColor} transition-all hover:scale-105 hover:shadow-lg animate-fade-in`}
                      style={{ animationDelay: `${0.05 * index}s` }}
                    >
                      <div className="relative aspect-[4/3]">
                        {group.image ? (
                          <img 
                            src={group.image} 
                            alt={`Study with ${group.name}`}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        ) : (
                          <div className={`w-full h-full bg-gradient-to-br ${group.gradient} flex items-center justify-center`}>
                            <Music className="w-10 h-10 text-primary/60" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                          <h3 className="font-display text-lg font-bold text-white group-hover:text-primary transition-colors">
                            {group.name}
                          </h3>
                          <p className="text-xs text-white/70 mt-0.5">
                            {group.fandom}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
            
            <div className="text-center mt-8">
              <p className="text-muted-foreground">
                Showing {filteredGroups.length} of {kpopGroups.length} K-Pop study rooms
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Study with K-Pop */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Why Study With <span className="text-gradient">K-Pop?</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {[
                {
                  icon: <Heart className="w-6 h-6 text-primary" />,
                  title: "Fandom Motivation",
                  desc: "Channel your love for your favorite artists into productive energy. Study hard like your idols work hard."
                },
                {
                  icon: <Users className="w-6 h-6 text-primary" />,
                  title: "Community Support",
                  desc: "Connect with fellow fans who share your passion. Motivate each other and build study friendships."
                },
                {
                  icon: <Music className="w-6 h-6 text-primary" />,
                  title: "Themed Ambiance",
                  desc: "Immerse yourself in an atmosphere inspired by your favorite group while staying focused on your goals."
                },
                {
                  icon: <Sparkles className="w-6 h-6 text-primary" />,
                  title: "Achieve Your Dreams",
                  desc: "Just like your idols pursue their dreams, use CSW to pursue yours with dedication and focus."
                },
              ].map((item, index) => (
                <Card 
                  key={index} 
                  className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all animate-fade-in"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
                    {item.icon}
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study with <span className="text-gradient">Your Fandom?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of K-Pop fans who study together on CSW. Free forever, no limits, just pure productivity with your favorite artists as inspiration.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Start Studying Free <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudyWithKpop;
